import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close, ContentCopy, Check } from "@mui/icons-material";

interface InvitePartnerModalProps {
  open: boolean;
  onClose: () => void;
  inviteLink?: string;
  color400?: string;
  color10?: string;
}

export const InvitePartnerModal: React.FC<InvitePartnerModalProps> = ({
  open,
  onClose,
  inviteLink = "https://muzukuru.com/referral-program",
  color400 = "#724E41",
  color10 = "#C29363",
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3.5,
          pt: 3.5,
          pb: 3,
          position: "relative",
          background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            bgcolor: "rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.8)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: "rgba(255,255,255,0.6)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 0.75,
          }}
        >
          Referral program
        </Typography>
        <Typography
          sx={{ fontSize: 20, fontWeight: 500, color: "#fff", mb: 0.5 }}
        >
          Invite a partner
        </Typography>
        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }}>
          Share your sign-up link with potential partners
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3.5, pt: 3, pb: 3.5 }}>
        {/* Link row */}
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: "text.disabled",
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Your invite link
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "action.hover",
            border: "0.5px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontFamily: "monospace",
              color: "text.secondary",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {inviteLink}
          </Box>
          <Tooltip title={copied ? "Copied!" : "Copy"}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                color: copied ? "#00b894" : "text.secondary",
                flexShrink: 0,
              }}
            >
              {copied ? (
                <Check sx={{ fontSize: 16 }} />
              ) : (
                <ContentCopy sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Share options */}
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: "text.disabled",
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            mt: 3,
            mb: 1.5,
          }}
        >
          Share via
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* WhatsApp */}
          <Box
            component="a"
            href={`https://wa.me/?text=${encodeURIComponent(
              "Join our partner program: " + inviteLink,
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 2,
              px: 1,
              bgcolor: "action.hover",
              border: "0.5px solid",
              borderColor: "divider",
              borderRadius: 3,
              textDecoration: "none",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "action.selected" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#e8f5e9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </Box>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}
            >
              WhatsApp
            </Typography>
          </Box>

          {/* Email */}
          <Box
            component="a"
            href={`mailto:?subject=${encodeURIComponent(
              "Join our partner program",
            )}&body=${encodeURIComponent(
              "Hi,\n\nSign up here: " + inviteLink,
            )}`}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 2,
              px: 1,
              bgcolor: "action.hover",
              border: "0.5px solid",
              borderColor: "divider",
              borderRadius: 3,
              textDecoration: "none",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "action.selected" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#e8eaf6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                color: "#3949ab",
              }}
            >
              ✉
            </Box>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}
            >
              Email
            </Typography>
          </Box>

          {/* Copy */}
          <Box
            onClick={handleCopy}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 2,
              px: 1,
              bgcolor: "action.hover",
              border: "0.5px solid",
              borderColor: "divider",
              borderRadius: 3,
              cursor: "pointer",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "action.selected" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#fce4ec",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#e91e63",
              }}
            >
              {copied ? (
                <Check sx={{ fontSize: 20 }} />
              ) : (
                <ContentCopy sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}
            >
              {copied ? "Copied!" : "Copy link"}
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            color: "text.disabled",
            mt: 2.5,
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          Partners who sign up through your link will be linked to your program
          automatically.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};
